import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ActionIcon,
  Alert,
  Button,
  Card,
  FileButton,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
  Textarea,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconFileText, IconTrash, IconUpload } from "@tabler/icons-react";
import EcosystemHeadQuestionnaireFillView from "@/components/ecosystem-head/EcosystemHeadQuestionnaireFillView";
import EcosystemHeadQuestionnaireSubmitConfirmModal from "@/components/ecosystem-head/EcosystemHeadQuestionnaireSubmitConfirmModal";
import { useUserContext } from "@/context/UserContext";
import { isClusterSignupUser } from "@/shared/constants/clientOnboarding.const";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { errorNotification } from "@/shared/utils/errorNotification";
import { successNotification } from "@/shared/utils/successNotification";
import {
  activeFillQuestionnaireForm,
  isAnswerableEcosystemHeadQuestion,
  isEcosystemHeadQuestionAnswerComplete,
  type EcosystemHeadQuestion,
  type EcosystemHeadQuestionnaireLanguages,
  type EcosystemHeadQuestionnaireForm,
  type EcosystemHeadSection,
} from "@shared/ecosystemHeadQuestionnaire";

type QuestionnaireLink = {
  id: string;
  status: string;
  verificationStatus: string;
  moreInfoMessage?: string | null;
  moreInfoResponse?: string | null;
  moreInfoDocuments?: string | null;
  moreInfoRespondedAt?: string | null;
};

type EcosystemHeadCompanyQuestionnaireModalProps = {
  linkId: string | null;
  opened: boolean;
  onClose: () => void;
  onUpdated?: () => void | Promise<void>;
};

const MOBILE_MODAL_TOP_OFFSET = "calc(var(--app-shell-header-height, 60px) + 12px)";

function parseMoreInfoDocuments(raw: string | null | undefined): Array<{ fileId: string; fileName: string }> {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as Array<{ fileId: string; fileName: string }>;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export default function EcosystemHeadCompanyQuestionnaireModal({
  linkId,
  opened,
  onClose,
  onUpdated,
}: EcosystemHeadCompanyQuestionnaireModalProps) {
  const isMobile = useMediaQuery("(max-width: 47.99em)", false, { getInitialValueInEffect: true });
  const { userDetails } = useUserContext();
  const companyId = userDetails?.company?.id;

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [moreInfoSubmitting, setMoreInfoSubmitting] = useState(false);
  const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false);
  const [detail, setDetail] = useState<{
    link: QuestionnaireLink;
    questions: EcosystemHeadQuestion[];
    sections?: EcosystemHeadSection[];
    languages?: EcosystemHeadQuestionnaireLanguages | null;
    forms?: Record<string, EcosystemHeadQuestionnaireForm> | null;
    defaultLanguageId?: string | null;
    submittedAt: string | null;
    answers?: Record<string, string>;
  } | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [fillLanguageId, setFillLanguageId] = useState<string | undefined>();
  const [moreInfoResponse, setMoreInfoResponse] = useState("");
  const [moreInfoDocuments, setMoreInfoDocuments] = useState<Array<{ fileId: string; fileName: string }>>([]);
  const [moreInfoUploading, setMoreInfoUploading] = useState(false);
  const moreInfoUploadResetRef = useRef<() => void>(null);

  const load = useCallback(async () => {
    if (!linkId) return;
    setLoading(true);
    try {
      const res = await trpc.ecosystemHead.getCompanyQuestionnaire.query({ linkId });
      setDetail(res);
      setAnswers(res.answers ?? {});
      setFillLanguageId(res.defaultLanguageId ?? undefined);
      setMoreInfoResponse(res.link.moreInfoResponse ?? "");
      setMoreInfoDocuments(parseMoreInfoDocuments(res.link.moreInfoDocuments));
    } catch (err) {
      handleTrpcError(err);
      setDetail(null);
      setAnswers({});
      setFillLanguageId(undefined);
      setMoreInfoResponse("");
      setMoreInfoDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [linkId]);

  useEffect(() => {
    if (!opened || !linkId) return;
    void load();
  }, [opened, linkId, load]);

  const { canSubmit: canSubmitQuestionnaire, remaining } = useMemo(() => {
    const questions = activeFillQuestionnaireForm(
      detail?.forms,
      fillLanguageId,
      { questions: detail?.questions ?? [], sections: detail?.sections ?? [] },
    ).questions;
    const answerable = questions.filter(isAnswerableEcosystemHeadQuestion);
    const incomplete = answerable.filter(
      (question) => !isEcosystemHeadQuestionAnswerComplete(question, answers[question.id], answers),
    );
    return {
      canSubmit: answerable.length > 0 && incomplete.length === 0,
      remaining: incomplete.length,
    };
  }, [detail?.forms, detail?.questions, detail?.sections, fillLanguageId, answers]);

  const handleSubmitQuestionnaire = async () => {
    if (!linkId || !canSubmitQuestionnaire) return;
    setSubmitting(true);
    try {
      await trpc.ecosystemHead.submitCompanyQuestionnaire.mutate({
        linkId,
        answers,
        languageId: fillLanguageId,
      });
      successNotification({
        title: "Questionnaire submitted",
        message: "Your answers have been sent to the Ecosystem Head.",
      });
      setSubmitConfirmOpen(false);
      await load();
      await onUpdated?.();
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleMoreInfoDocUpload = async (file: File | null) => {
    if (!file) return;
    if (!companyId) {
      errorNotification({ title: "Upload unavailable", message: "Company profile is required to upload documents." });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      errorNotification({ title: "File too large", message: "Maximum file size is 10 MB." });
      moreInfoUploadResetRef.current?.();
      return;
    }
    setMoreInfoUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("category", "COMPANY_DOCUMENT");
      formData.append("companyId", companyId);
      formData.append("name", file.name);
      const uploaded = await trpc.file.upload.mutate(formData as unknown as FormData);
      setMoreInfoDocuments((prev) => [...prev, { fileId: uploaded.id, fileName: file.name }]);
    } catch (err) {
      console.error(err);
      errorNotification({ title: "Upload failed", message: "Could not upload the document. Try again." });
    } finally {
      moreInfoUploadResetRef.current?.();
      setMoreInfoUploading(false);
    }
  };

  const handleSubmitMoreInfo = async () => {
    if (!linkId || !moreInfoResponse.trim()) return;
    setMoreInfoSubmitting(true);
    try {
      await trpc.ecosystemHead.submitCompanyMoreInfoResponse.mutate({
        linkId,
        response: moreInfoResponse.trim(),
        ...(moreInfoDocuments.length > 0 ? { documents: moreInfoDocuments } : {}),
      });
      successNotification({
        title: "Response submitted",
        message: "Your additional information has been sent to the Ecosystem Head.",
      });
      await load();
      await onUpdated?.();
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setMoreInfoSubmitting(false);
    }
  };

  const verificationStatus = detail?.link.verificationStatus;
  const canEditQuestionnaire = verificationStatus === "AWAITING_QUESTIONNAIRE";
  const isMoreInfoRequested = verificationStatus === "MORE_INFO_REQUESTED";
  const hasMoreInfoRequest = Boolean(detail?.link.moreInfoMessage?.trim());
  const hasMoreInfoResponse = Boolean(detail?.link.moreInfoResponse?.trim());
  const canSubmitMoreInfo = isMoreInfoRequested && hasMoreInfoRequest && !hasMoreInfoResponse;
  const requireCompletion =
    isClusterSignupUser(userDetails) && (loading || canEditQuestionnaire || canSubmitMoreInfo);
  const showQuestionnaireAnswers = Boolean(detail?.submittedAt) || canEditQuestionnaire;
  const modalTitle = canSubmitMoreInfo ? "Additional information requested" : "Onboarding questionnaire";
  const moreInfoDocumentsSubmitted = parseMoreInfoDocuments(detail?.link.moreInfoDocuments);

  const handleClose = () => {
    if (submitting || moreInfoSubmitting) return;
    if (requireCompletion) return;
    setSubmitConfirmOpen(false);
    onClose();
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={handleClose}
        title={modalTitle}
        centered={!isMobile}
        size="min(96vw, 1400px)"
        zIndex={1200}
        withCloseButton={!requireCompletion}
        closeOnClickOutside={!requireCompletion}
        closeOnEscape={!requireCompletion}
        styles={{
          inner: isMobile
            ? {
                alignItems: "flex-start",
                paddingTop: MOBILE_MODAL_TOP_OFFSET,
                paddingBottom: 12,
                paddingInline: 12,
              }
            : undefined,
          content: { width: "100%" },
          body: {
            maxHeight: isMobile
              ? `calc(100dvh - var(--app-shell-header-height, 60px) - 84px)`
              : "80vh",
            overflowY: "auto",
          },
        }}
      >
        {loading ? (
          <Group justify="center" py="xl">
            <Loader />
          </Group>
        ) : !detail ? (
          <Text size="sm" c="dimmed">
            Questionnaire not available.
          </Text>
        ) : (
          <Stack gap="md">
            {canSubmitMoreInfo ? (
              <Stack gap="md">
                <Alert color="blue">
                  The Ecosystem Head needs more information before approving your company.
                </Alert>
                <Alert color="orange" title="Request from Ecosystem Head">
                  {detail.link.moreInfoMessage}
                </Alert>
                <Stack gap="sm">
                  <Text size="sm" fw={600}>
                    Your response *
                  </Text>
                  <Textarea
                    placeholder="Provide the additional information requested by the Ecosystem Head..."
                    value={moreInfoResponse}
                    onChange={(event) => setMoreInfoResponse(event.currentTarget.value)}
                    minRows={4}
                    autosize
                  />
                  <Text size="sm" fw={600}>
                    Supporting documents (optional) (You can upload multiple documents)
                  </Text>
                  {moreInfoDocuments.length > 0 ? (
                    <Stack gap="xs">
                      {moreInfoDocuments.map((doc) => (
                        <Group key={doc.fileId} justify="space-between" wrap="nowrap">
                          <Group gap="xs" wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
                            <IconFileText size={16} color="#6B7280" style={{ flexShrink: 0 }} />
                            <Text size="sm" lineClamp={1}>
                              {doc.fileName}
                            </Text>
                          </Group>
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            onClick={() =>
                              setMoreInfoDocuments((prev) => prev.filter((d) => d.fileId !== doc.fileId))
                            }
                            aria-label="Remove document"
                          >
                            <IconTrash size={14} />
                          </ActionIcon>
                        </Group>
                      ))}
                    </Stack>
                  ) : null}
                  <FileButton
                    resetRef={moreInfoUploadResetRef}
                    onChange={(file) => void handleMoreInfoDocUpload(file)}
                    accept="application/pdf,image/png,image/jpeg,image/webp"
                    disabled={moreInfoUploading}
                  >
                    {(props) => (
                      <Button
                        {...props}
                        variant="default"
                        size="sm"
                        leftSection={moreInfoUploading ? <Loader size={14} /> : <IconUpload size={14} />}
                        w="fit-content"
                      >
                        {moreInfoUploading ? "Uploading..." : "Upload document"}
                      </Button>
                    )}
                  </FileButton>
                </Stack>
                <Group justify="flex-end">
                  {!requireCompletion ? (
                    <Button variant="default" onClick={handleClose} disabled={moreInfoSubmitting}>
                      Cancel
                    </Button>
                  ) : null}
                  <Button
                    onClick={() => void handleSubmitMoreInfo()}
                    loading={moreInfoSubmitting}
                    disabled={!moreInfoResponse.trim()}
                  >
                    Submit response
                  </Button>
                </Group>
              </Stack>
            ) : null}

            {!canSubmitMoreInfo && hasMoreInfoRequest ? (
              <Alert color="orange" title="Extra question from Ecosystem Head">
                {detail.link.moreInfoMessage}
              </Alert>
            ) : null}

            {hasMoreInfoResponse ? (
              <Card withBorder radius="md" p="md" bg="gray.0">
                <Text size="sm" fw={600} mb={6}>
                  Your extra question response
                </Text>
                <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
                  {detail.link.moreInfoResponse}
                </Text>
                {moreInfoDocumentsSubmitted.length > 0 ? (
                  <Stack gap={4} mt="sm">
                    <Text size="xs" fw={600} c="dimmed">
                      Attached documents
                    </Text>
                    {moreInfoDocumentsSubmitted.map((doc) => (
                      <Group key={doc.fileId} gap="xs" wrap="nowrap">
                        <IconFileText size={14} color="#6B7280" />
                        <Text size="xs" c="dimmed">
                          {doc.fileName}
                        </Text>
                      </Group>
                    ))}
                  </Stack>
                ) : null}
                {detail.link.moreInfoRespondedAt ? (
                  <Text size="xs" c="dimmed" mt={6}>
                    Submitted on {new Date(detail.link.moreInfoRespondedAt).toLocaleString()}
                  </Text>
                ) : null}
              </Card>
            ) : null}

            {!canSubmitMoreInfo && detail.submittedAt ? (
              <Alert color="green">
                Questionnaire submitted on {new Date(detail.submittedAt).toLocaleString()}
              </Alert>
            ) : !canSubmitMoreInfo && canEditQuestionnaire ? (
              <Alert color="blue">Complete all required questions to finish onboarding.</Alert>
            ) : null}

            {!canSubmitMoreInfo ? (
              <>
                {showQuestionnaireAnswers && detail.submittedAt ? (
                  <Text size="sm" fw={600} c="dimmed">
                    Your questionnaire answers
                  </Text>
                ) : null}
                {showQuestionnaireAnswers || canEditQuestionnaire ? (
                  <EcosystemHeadQuestionnaireFillView
                    questions={detail.questions}
                    sections={detail.sections}
                    forms={detail.forms}
                    defaultLanguageId={detail.defaultLanguageId}
                    answers={answers}
                    languages={detail.languages ?? null}
                    readOnly={!canEditQuestionnaire}
                    columns={2}
                    onFillLanguageChange={setFillLanguageId}
                    onChange={(questionId, value) => {
                      setAnswers((prev) => ({ ...prev, [questionId]: value }));
                    }}
                  />
                ) : null}
              </>
            ) : null}

            {canEditQuestionnaire && !canSubmitMoreInfo ? (
              <Group justify="space-between" mt="md" wrap="wrap" gap="sm">
                <Text size="sm" c="dimmed">
                  {canSubmitQuestionnaire
                    ? "All set! You can submit now."
                    : `${remaining} question${remaining === 1 ? "" : "s"} left`}
                </Text>
                <Group gap="sm">
                  {!requireCompletion ? (
                    <Button variant="default" onClick={handleClose} disabled={submitting}>
                      Cancel
                    </Button>
                  ) : null}
                  <Button
                    onClick={() => setSubmitConfirmOpen(true)}
                    loading={submitting}
                    disabled={!canSubmitQuestionnaire || submitting}
                  >
                    Submit questionnaire
                  </Button>
                </Group>
              </Group>
            ) : !canSubmitMoreInfo ? (
              <Group justify="flex-end" mt="md">
                <Button variant="default" onClick={handleClose}>
                  Close
                </Button>
              </Group>
            ) : null}
          </Stack>
        )}
      </Modal>

      <EcosystemHeadQuestionnaireSubmitConfirmModal
        opened={submitConfirmOpen}
        onClose={() => setSubmitConfirmOpen(false)}
        onConfirm={() => void handleSubmitQuestionnaire()}
        loading={submitting}
      />
    </>
  );
}
